import { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography,
    useTheme,
    Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import { tokens } from '../../themes/themes';
import EditorDialog from './EditorDialog';
import PreviewDialog from './PreviewDialog';

const STORAGE_KEY = 'zeta_email_templates';

const defaultDesign = (bodyRows) => ({
    counters: { u_row: bodyRows.length + 1, u_column: bodyRows.length + 2, u_content_text: bodyRows.length + 3 },
    body: {
        id: 'default',
        rows: bodyRows,
        values: {
            popupPosition: 'center',
            popupWidth: '600px',
            popupHeight: 'auto',
            borderRadius: '10px',
            contentAlign: 'center',
            contentVerticalAlign: 'center',
            contentWidth: '600px',
            fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
            textColor: '#333333',
            popupBackgroundColor: '#FFFFFF',
            popupBackgroundImage: { url: '', fullWidth: true, repeat: 'no-repeat', size: 'cover', position: 'center' },
            popupOverlay_backgroundColor: 'rgba(0,0,0,0.1)',
            popupCloseButton_position: 'top-right',
            popupCloseButton_backgroundColor: '#DDDDDD',
            popupCloseButton_iconColor: '#000000',
            popupCloseButton_borderRadius: '0px',
            popupCloseButton_margin: '0px',
            popupCloseButton_action: { name: 'close_popup', attrs: { onClick: "document.querySelector('.u-teleport').remove();" } },
            backgroundColor: '#F5F5F5',
            backgroundImage: { url: '', fullWidth: true, repeat: 'no-repeat', size: 'cover', position: 'center' },
            preheaderText: '',
            linkStyle: { body: true, linkColor: '#0364ff', linkHoverColor: '#0364ff', linkUnderline: true, linkHoverUnderline: true },
            _meta: { htmlID: 'u_body', htmlClassNames: 'u_body' }
        }
    }
});

const makeRow = (bgColor, columns) => ({
    id: `r_${Math.random().toString(36).slice(2)}`,
    cells: columns.map(() => 1),
    columns: columns.map(col => ({
        id: `c_${Math.random().toString(36).slice(2)}`,
        contents: col,
        values: { backgroundColor: '', padding: '0px', border: {}, borderRadius: '0px', _meta: { htmlID: `u_column_${Math.random().toString(36).slice(2)}`, htmlClassNames: 'u_column' } }
    })),
    values: {
        displayCondition: null,
        columns: false,
        backgroundColor: bgColor,
        columnsBackgroundColor: '',
        backgroundImage: { url: '', fullWidth: true, repeat: 'no-repeat', size: 'cover', position: 'center' },
        padding: '0px',
        anchor: '',
        hideDesktop: false,
        _meta: { htmlID: `u_row_${Math.random().toString(36).slice(2)}`, htmlClassNames: 'u_row' },
        selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true
    }
});

const textBlock = (html, padding = '20px 40px') => ({
    id: `ct_${Math.random().toString(36).slice(2)}`,
    type: 'text',
    values: {
        containerPadding: padding,
        anchor: '',
        textAlign: 'left',
        lineHeight: '160%',
        linkStyle: { inherit: true, linkColor: '#0000ee', linkHoverColor: '#0000ee', linkUnderline: true, linkHoverUnderline: true },
        hideDesktop: false,
        displayCondition: null,
        _meta: { htmlID: `u_content_${Math.random().toString(36).slice(2)}`, htmlClassNames: 'u_content_text' },
        selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true,
        text: html
    }
});

const buttonBlock = (text, href = '#', bgColor = '#0364ff', padding = '20px 40px') => ({
    id: `cb_${Math.random().toString(36).slice(2)}`,
    type: 'button',
    values: {
        containerPadding: padding,
        anchor: '',
        href: { name: 'web', values: { href, target: '_blank' } },
        buttonColors: { color: '#FFFFFF', backgroundColor: bgColor, hoverColor: '#FFFFFF', hoverBackgroundColor: '#0250cc' },
        size: { autoWidth: true, width: '100%' },
        textAlign: 'center',
        lineHeight: '120%',
        padding: '12px 28px',
        border: {},
        borderRadius: '6px',
        hideDesktop: false,
        displayCondition: null,
        _meta: { htmlID: `u_content_${Math.random().toString(36).slice(2)}`, htmlClassNames: 'u_content_button' },
        selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true,
        text: `<span style="font-size: 14px; font-family: arial,helvetica,sans-serif; font-weight: 700;">${text}</span>`,
        calculatedWidth: 180, calculatedHeight: 42
    }
});

const dividerBlock = (padding = '10px 40px') => ({
    id: `cd_${Math.random().toString(36).slice(2)}`,
    type: 'divider',
    values: {
        containerPadding: padding,
        anchor: '',
        border: { borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: '#E0E0E0' },
        textAlign: 'center',
        hideDesktop: false,
        displayCondition: null,
        _meta: { htmlID: `u_content_${Math.random().toString(36).slice(2)}`, htmlClassNames: 'u_content_divider' },
        selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true
    }
});

const defaultTemplates = [
    {
        id: 1,
        nombre: 'Bienvenida - Cliente Nuevo',
        descripcion: 'Email de bienvenida para nuevos clientes con información inicial del servicio',
        design: defaultDesign([
            makeRow('#0364ff', [
                [textBlock('<h1 style="text-align: center; color: #ffffff; margin: 0;"><strong>¡Bienvenido/a!</strong></h1>', '30px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 16px; color: #333333;">Hola <strong>{{nombre_cliente}}</strong>,</p><p style="font-size: 14px; color: #555555;">Nos alegra mucho que formes parte de nuestra familia. Tu cuenta ha sido creada exitosamente y ya podés disfrutar de todos nuestros servicios.</p>', '30px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 14px; color: #555555;"><strong>📋 Datos de tu cuenta:</strong></p><ul style="font-size: 14px; color: #555555; line-height: 200%;"><li>N° de Cliente: <strong>{{nro_cliente}}</strong></li><li>Plan contratado: <strong>{{plan}}</strong></li><li>Fecha de alta: <strong>{{fecha_alta}}</strong></li></ul>', '10px 40px 10px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 14px; color: #555555;">Si tenés alguna consulta, no dudes en comunicarte con nuestro equipo de soporte.</p>', '10px 40px')]
            ]),
            makeRow('#ffffff', [
                [buttonBlock('Acceder a Mi Cuenta', '#', '#0364ff', '10px 40px 30px 40px')]
            ]),
            makeRow('#f0f0f0', [
                [textBlock('<p style="font-size: 12px; color: #999999; text-align: center;">Este correo fue enviado automáticamente. Por favor no respondas a este mensaje.<br/>© {{anio}} - Todos los derechos reservados.</p>', '20px 40px')]
            ])
        ]),
        html: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    {
        id: 2,
        nombre: 'Notificación de Ticket',
        descripcion: 'Notificación automática cuando se crea o actualiza un ticket de soporte',
        design: defaultDesign([
            makeRow('#1a1a2e', [
                [textBlock('<h1 style="text-align: center; color: #ffffff; margin: 0;"><strong>🎫 Actualización de Ticket</strong></h1>', '30px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 16px; color: #333333;">Hola <strong>{{nombre_cliente}}</strong>,</p><p style="font-size: 14px; color: #555555;">Te informamos que hubo una actualización en tu ticket de soporte. A continuación, los detalles:</p>', '30px 40px')]
            ]),
            makeRow('#f8f9fa', [
                [textBlock('<table style="width: 100%; font-size: 14px; color: #555555; border-collapse: collapse;"><tr><td style="padding: 8px 0; font-weight: bold; width: 40%;">N° de Ticket:</td><td style="padding: 8px 0;">{{nro_ticket}}</td></tr><tr><td style="padding: 8px 0; border-top: 1px solid #e0e0e0; font-weight: bold;">Motivo:</td><td style="padding: 8px 0; border-top: 1px solid #e0e0e0;">{{motivo}}</td></tr><tr><td style="padding: 8px 0; border-top: 1px solid #e0e0e0; font-weight: bold;">Estado:</td><td style="padding: 8px 0; border-top: 1px solid #e0e0e0;"><span style="background: #4caf50; color: white; padding: 3px 12px; border-radius: 12px; font-size: 12px;">{{estado}}</span></td></tr><tr><td style="padding: 8px 0; border-top: 1px solid #e0e0e0; font-weight: bold;">Fecha:</td><td style="padding: 8px 0; border-top: 1px solid #e0e0e0;">{{fecha}}</td></tr></table>', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 14px; color: #555555;"><strong>Comentario:</strong></p><p style="font-size: 14px; color: #666666; background: #f5f5f5; padding: 15px; border-radius: 8px; border-left: 4px solid #0364ff;">{{comentario}}</p>', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [buttonBlock('Ver Ticket Completo', '#', '#1a1a2e', '10px 40px 30px 40px')]
            ]),
            makeRow('#f0f0f0', [
                [textBlock('<p style="font-size: 12px; color: #999999; text-align: center;">Este correo fue enviado automáticamente. Por favor no respondas a este mensaje.<br/>© {{anio}} - Todos los derechos reservados.</p>', '20px 40px')]
            ])
        ]),
        html: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    {
        id: 3,
        nombre: 'Recordatorio de Pago',
        descripcion: 'Aviso de vencimiento próximo o factura pendiente de pago',
        design: defaultDesign([
            makeRow('#e65100', [
                [textBlock('<h1 style="text-align: center; color: #ffffff; margin: 0;"><strong>⚠️ Recordatorio de Pago</strong></h1>', '30px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 16px; color: #333333;">Estimado/a <strong>{{nombre_cliente}}</strong>,</p><p style="font-size: 14px; color: #555555;">Te recordamos que tenés una factura próxima a vencer. Te pedimos que regularices tu situación para evitar inconvenientes con tu servicio.</p>', '30px 40px')]
            ]),
            makeRow('#fff3e0', [
                [textBlock('<table style="width: 100%; font-size: 14px; color: #555555; border-collapse: collapse;"><tr><td style="padding: 10px 0; font-weight: bold; width: 40%;">N° de Factura:</td><td style="padding: 10px 0;">{{nro_factura}}</td></tr><tr><td style="padding: 10px 0; border-top: 1px solid #ffe0b2; font-weight: bold;">Monto:</td><td style="padding: 10px 0; border-top: 1px solid #ffe0b2; font-size: 18px; font-weight: bold; color: #e65100;">${{monto}}</td></tr><tr><td style="padding: 10px 0; border-top: 1px solid #ffe0b2; font-weight: bold;">Vencimiento:</td><td style="padding: 10px 0; border-top: 1px solid #ffe0b2; color: #e65100; font-weight: bold;">{{fecha_vencimiento}}</td></tr></table>', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [buttonBlock('Pagar Ahora', '#', '#e65100', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 13px; color: #888888; text-align: center;">Si ya realizaste el pago, por favor desestimá este correo. El mismo puede demorar hasta 72hs hábiles en impactarse.</p>', '10px 40px 20px 40px')]
            ]),
            makeRow('#f0f0f0', [
                [textBlock('<p style="font-size: 12px; color: #999999; text-align: center;">Este correo fue enviado automáticamente. Por favor no respondas a este mensaje.<br/>© {{anio}} - Todos los derechos reservados.</p>', '20px 40px')]
            ])
        ]),
        html: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    {
        id: 4,
        nombre: 'Promoción / Upgrade',
        descripcion: 'Template para comunicar promociones, upgrades o nuevos planes disponibles',
        design: defaultDesign([
            makeRow('#6a1b9a', [
                [textBlock('<h1 style="text-align: center; color: #ffffff; margin: 0;"><strong>🚀 ¡Oferta Especial!</strong></h1><p style="text-align: center; color: #e1bee7; font-size: 14px; margin: 5px 0 0 0;">Solo por tiempo limitado</p>', '30px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 16px; color: #333333;">Hola <strong>{{nombre_cliente}}</strong>,</p><p style="font-size: 14px; color: #555555;">Tenemos una propuesta exclusiva para vos. ¡Mejorá tu plan actual y disfrutá de más velocidad y beneficios!</p>', '30px 40px')]
            ]),
            makeRow('#f3e5f5', [
                [textBlock('<div style="text-align: center;"><p style="font-size: 13px; color: #888; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 5px;">Tu plan actual</p><p style="font-size: 24px; color: #6a1b9a; font-weight: bold; margin: 0;">{{plan_actual}}</p></div>', '20px 40px 10px 40px')]
            ]),
            makeRow('#f3e5f5', [
                [textBlock('<div style="text-align: center; font-size: 24px; color: #6a1b9a;">⬇️</div>', '0px')]
            ]),
            makeRow('#f3e5f5', [
                [textBlock('<div style="text-align: center; background: white; border-radius: 12px; padding: 20px; margin: 0 20px; box-shadow: 0 2px 12px rgba(106,27,154,0.15);"><p style="font-size: 13px; color: #888; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 5px;">Plan recomendado</p><p style="font-size: 28px; color: #6a1b9a; font-weight: bold; margin: 0;">{{plan_nuevo}}</p><p style="font-size: 14px; color: #555; margin-top: 10px;">{{beneficios}}</p></div>', '0px 40px 20px 40px')]
            ]),
            makeRow('#ffffff', [
                [buttonBlock('¡Quiero Mejorar Mi Plan!', '#', '#6a1b9a', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 13px; color: #888888; text-align: center;">*Promoción válida hasta el {{fecha_fin_promo}}. Sujeta a disponibilidad en tu zona.</p>', '10px 40px 20px 40px')]
            ]),
            makeRow('#f0f0f0', [
                [textBlock('<p style="font-size: 12px; color: #999999; text-align: center;">Este correo fue enviado automáticamente. Por favor no respondas a este mensaje.<br/>© {{anio}} - Todos los derechos reservados.</p>', '20px 40px')]
            ])
        ]),
        html: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    },
    {
        id: 5,
        nombre: 'Corte de Servicio',
        descripcion: 'Aviso de suspensión programada o corte del servicio por mantenimiento o falta de pago',
        design: defaultDesign([
            makeRow('#c62828', [
                [textBlock('<h1 style="text-align: center; color: #ffffff; margin: 0;"><strong>🔴 Aviso Importante</strong></h1>', '30px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 16px; color: #333333;">Estimado/a <strong>{{nombre_cliente}}</strong>,</p><p style="font-size: 14px; color: #555555;">Le informamos que su servicio será interrumpido por el siguiente motivo:</p>', '30px 40px')]
            ]),
            makeRow('#ffebee', [
                [textBlock('<div style="border-left: 4px solid #c62828; padding-left: 16px;"><p style="font-size: 14px; color: #333; font-weight: bold; margin-bottom: 5px;">Motivo: {{motivo_corte}}</p><p style="font-size: 14px; color: #555;">Fecha programada: <strong>{{fecha_corte}}</strong></p><p style="font-size: 14px; color: #555;">Duración estimada: <strong>{{duracion}}</strong></p></div>', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [textBlock('<p style="font-size: 14px; color: #555555;">Para evitar la interrupción del servicio o si tenés consultas, por favor contactate con nuestro equipo de atención.</p>', '20px 40px')]
            ]),
            makeRow('#ffffff', [
                [buttonBlock('Contactar Soporte', '#', '#c62828', '10px 40px 30px 40px')]
            ]),
            makeRow('#f0f0f0', [
                [textBlock('<p style="font-size: 12px; color: #999999; text-align: center;">Este correo fue enviado automáticamente. Por favor no respondas a este mensaje.<br/>© {{anio}} - Todos los derechos reservados.</p>', '20px 40px')]
            ])
        ]),
        html: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    }
];

const loadTemplates = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.length > 0) return parsed;
        }
        // Cargar plantillas por defecto
        saveTemplates(defaultTemplates);
        return defaultTemplates;
    } catch {
        saveTemplates(defaultTemplates);
        return defaultTemplates;
    }
};

const saveTemplates = (templates) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
};

const TemplatesEmailScreen = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDarkMode = theme.palette.mode === 'dark';

    const [templates, setTemplates] = useState(loadTemplates);
    const [editorOpen, setEditorOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);

    const [form, setForm] = useState({ id: null, nombre: '', descripcion: '' });
    const [formErrors, setFormErrors] = useState({});

    const isEditing = form.id !== null;

    const openNewEditor = () => {
        setForm({ id: null, nombre: '', descripcion: '' });
        setFormErrors({});
        setEditorOpen(true);
    };

    const openEditEditor = (tpl) => {
        setForm({ id: tpl.id, nombre: tpl.nombre, descripcion: tpl.descripcion });
        setFormErrors({});
        setEditorOpen(true);
    };

    const handleEditorSave = (design, html) => {
        let updated;
        if (isEditing) {
            updated = templates.map((t) =>
                t.id === form.id
                    ? { ...t, nombre: form.nombre, descripcion: form.descripcion, design, html, updatedAt: new Date().toISOString() }
                    : t
            );
        } else {
            const newTpl = {
                id: Date.now(),
                nombre: form.nombre,
                descripcion: form.descripcion,
                design,
                html,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            updated = [...templates, newTpl];
        }
        saveTemplates(updated);
        setTemplates(updated);
        setEditorOpen(false);
    };

    const handlePreview = (tpl) => {
        if (tpl.html) {
            setPreviewHtml(tpl.html);
            setPreviewOpen(true);
        } else if (tpl.design) {
            // Si no tiene HTML pero tiene design, abrir editor oculto para exportar
            setPreviewHtml('');
            setPreviewOpen(true);
            // Montar editor temporal para exportar HTML
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'fixed';
            tempContainer.style.left = '-9999px';
            tempContainer.style.width = '1px';
            tempContainer.style.height = '1px';
            tempContainer.style.overflow = 'hidden';
            document.body.appendChild(tempContainer);
            const tempIframe = document.createElement('iframe');
            tempContainer.appendChild(tempIframe);

            import('react-email-editor').then(() => {
                // Usar el approach más simple: construir HTML del design manualmente
                const rows = tpl.design?.body?.rows || [];
                let builtHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>body{margin:0;padding:0;font-family:arial,helvetica,sans-serif;background:#F5F5F5;}table{border-collapse:collapse;}</style></head><body><center><table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;">`;
                rows.forEach(row => {
                    const bgColor = row.values?.backgroundColor || '#ffffff';
                    builtHtml += `<tr><td style="background-color:${bgColor};">`;
                    row.columns?.forEach(col => {
                        col.contents?.forEach(content => {
                            if (content.type === 'paragraph' || content.type === 'text') {
                                const pad = content.values?.containerPadding || '20px';
                                builtHtml += `<div style="padding:${pad};line-height:160%;">${content.values?.text || ''}</div>`;
                            } else if (content.type === 'button') {
                                const pad = content.values?.containerPadding || '20px';
                                const btnBg = content.values?.buttonColors?.backgroundColor || '#0364ff';
                                const btnColor = content.values?.buttonColors?.color || '#FFFFFF';
                                const btnRadius = content.values?.borderRadius || '6px';
                                const btnPad = content.values?.padding || '12px 28px';
                                builtHtml += `<div style="padding:${pad};text-align:center;"><a href="#" style="display:inline-block;background:${btnBg};color:${btnColor};padding:${btnPad};border-radius:${btnRadius};text-decoration:none;font-weight:bold;font-size:14px;">${content.values?.text || ''}</a></div>`;
                            } else if (content.type === 'divider') {
                                const pad = content.values?.containerPadding || '10px';
                                const borderColor = content.values?.border?.borderTopColor || '#E0E0E0';
                                builtHtml += `<div style="padding:${pad};"><hr style="border:none;border-top:1px solid ${borderColor};margin:0;"/></div>`;
                            }
                        });
                    });
                    builtHtml += `</td></tr>`;
                });
                builtHtml += `</table></center></body></html>`;

                document.body.removeChild(tempContainer);
                setPreviewHtml(builtHtml);

                // Guardar el HTML generado para futuras previews
                const updated = templates.map(t =>
                    t.id === tpl.id ? { ...t, html: builtHtml } : t
                );
                saveTemplates(updated);
                setTemplates(updated);
            });
        } else {
            setPreviewHtml('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:arial;color:#888;"><p>Esta plantilla no tiene contenido. Editala para generar la vista previa.</p></body></html>');
            setPreviewOpen(true);
        }
    };

    const confirmDelete = (tpl) => {
        setTemplateToDelete(tpl);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = () => {
        const updated = templates.filter((t) => t.id !== templateToDelete.id);
        saveTemplates(updated);
        setTemplates(updated);
        setDeleteConfirmOpen(false);
        setTemplateToDelete(null);
    };

    const formatDate = (iso) => {
        if (!iso) return '-';
        return new Date(iso).toLocaleDateString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <Box sx={{ p: 3, height: '100%' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ fontSize: 32, color: isDarkMode ? '#64B5F6' : '#0364ff' }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: isDarkMode ? '#e0e0e0' : '#1a1a2e', lineHeight: 1.2 }}>
                            Templates de Email
                        </Typography>
                        <Typography variant="body2" sx={{ color: isDarkMode ? '#9e9e9e' : '#6b7280' }}>
                            Creá y administrá plantillas de correo personalizadas
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openNewEditor}
                    sx={{
                        background: isDarkMode
                            ? 'linear-gradient(135deg, #1565c0, #0364ff)'
                            : 'linear-gradient(135deg, #0364ff, #1a73e8)',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        boxShadow: '0 4px 12px rgba(3,100,255,0.3)',
                        '&:hover': { boxShadow: '0 6px 18px rgba(3,100,255,0.45)' }
                    }}
                >
                    Nueva Plantilla
                </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Template list */}
            {templates.length === 0 ? (
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    py: 10, gap: 2,
                    color: isDarkMode ? '#616161' : '#9e9e9e'
                }}>
                    <EmailIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography variant="h6" sx={{ opacity: 0.5 }}>
                        No hay plantillas todavía
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.4 }}>
                        Hacé clic en "Nueva Plantilla" para empezar
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {templates.map((tpl) => (
                        <Grid item xs={12} sm={6} md={4} key={tpl.id}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                                    background: isDarkMode ? '#1e1e2e' : '#ffffff',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: isDarkMode
                                            ? '0 4px 20px rgba(0,0,0,0.4)'
                                            : '0 4px 20px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700, color: isDarkMode ? '#e0e0e0' : '#1a1a2e', lineHeight: 1.3 }}
                                    >
                                        {tpl.nombre}
                                    </Typography>
                                    <Chip
                                        label="Activa"
                                        size="small"
                                        color="success"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Box>
                                {tpl.descripcion && (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: isDarkMode ? '#9e9e9e' : '#6b7280', mb: 1.5, fontSize: '0.8rem' }}
                                    >
                                        {tpl.descripcion}
                                    </Typography>
                                )}
                                <Typography variant="caption" sx={{ color: isDarkMode ? '#616161' : '#9ca3af', display: 'block', mb: 2 }}>
                                    Actualizado: {formatDate(tpl.updatedAt)}
                                </Typography>
                                <Divider sx={{ mb: 1.5 }} />
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                    <Tooltip title="Vista previa">
                                        <IconButton size="small" onClick={() => handlePreview(tpl)}
                                            sx={{ color: isDarkMode ? '#80CBC4' : '#00897b' }}>
                                            <PreviewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={() => openEditEditor(tpl)}
                                            sx={{ color: isDarkMode ? '#64B5F6' : '#0364ff' }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar">
                                        <IconButton size="small" onClick={() => confirmDelete(tpl)}
                                            sx={{ color: isDarkMode ? '#ef9a9a' : '#e53935' }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Email Editor Dialog */}
            <EditorDialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                isEditing={isEditing}
                form={form}
                setForm={setForm}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                isDarkMode={isDarkMode}
                currentDesign={isEditing ? templates.find((t) => t.id === form.id)?.design : null}
                onSave={handleEditorSave}
            />

            {/* Preview Dialog */}
            <PreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                previewHtml={previewHtml}
            />

            {/* Delete Confirm Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Eliminar plantilla</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro que querés eliminar la plantilla{' '}
                        <strong>"{templateToDelete?.nombre}"</strong>? Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TemplatesEmailScreen;
